<template>
    <div v-if="activeProduct.pricing.price"
        class="space-y-xs">
        <div v-if="isPromotionActive && clubPrice && clubSaving"
            class="flex flex-col items-start justify-between gap-y-4 lg:justify-start xl:flex-row"
            :class="{
                'xl:items-start': isCountdownShown,
                'xl:items-end': !isCountdownShown
            }">
            <v-page-product-club-badge
                class="w-fit"
                :club-price="clubPrice"
                :club-saving="clubSaving" />

            <div class="hidden w-full md:block lg:w-2/3 xl:ml-xl xl:w-1/2">
                <v-page-product-promotion-info
                    :pending="!isMounted || pending"
                    :promotion-info="promotionInfo" />
            </div>
        </div>

        <div class="mb-xs flex flex-col items-start justify-start gap-y-4 xl:flex-row"
            :class="{
                'xl:items-start': isCountdownShown,
                'xl:items-end': !isCountdownShown,
            }">
            <template v-if="!clubPrice">
                <div class="flex flex-row items-end">
                    <v-h2 v-if="error"
                        class="lowercase text-red-500">
                        {{ t('product-part-payment-prefix') }}
                        {{ formatCurrency(activeProduct.pricing.price.amount) }}
                    </v-h2>

                    <v-h2 v-else
                        class="text-red-500">
                        {{ formatCurrency(activeProduct.pricing.price.amount) }}
                    </v-h2>

                    <p
                        v-if="activeProduct.pricing.originalPrice
                            && activeProduct.pricing.price.amount !== activeProduct.pricing.originalPrice.amount"
                        class="mb-xs ml-xs text-sm tracking-xl text-[#5A5454] line-through">
                        {{ formatCurrency(activeProduct.pricing.originalPrice.amount) }}
                    </p>
                </div>

                <div class="my-xs block w-full lg:w-2/3 xl:ml-xl xl:w-1/2"
                    :class="{
                        'my-xs md:mt-xs': isCountdownShown,
                        'my-xs items-end': !isCountdownShown
                    }">
                    <v-page-product-promotion-info
                        :pending="!isMounted || pending"
                        :promotion-info="promotionInfo" />
                </div>
            </template>

            <div v-if="activeProduct.pricing.originalPrice && clubPrice"
                class="flex flex-col">
                <p class="my-xs text-2xl tracking-xl text-[#5A5454] line-through">
                    {{ formatCurrency(activeProduct.pricing.originalPrice.amount) }}
                </p>

                <div class="block w-full md:hidden lg:w-2/3 xl:ml-xl xl:w-1/2"
                    :class="{
                        'my-xs md:mt-xs': promotionInfo.showEndingTimer && promotionInfo.showCountdownTextContent,
                        'mt-xs': !promotionInfo.showEndingTimer && promotionInfo.showCountdownTextContent
                    }">
                    <v-page-product-promotion-info
                        :pending="!isMounted || pending"
                        :promotion-info="promotionInfo" />
                </div>
            </div>
        </div>

        <div v-if="calculateLiterPrice !== undefined"
            class="flex flex-col">
            <p class="text-base tracking-xl text-[#5A5454]">
                {{ formatCurrency(calculateLiterPrice, { maximumFractionDigits: 2 }) }}/L
            </p>
        </div>

        <div class="flex flex-col items-start">
            <v-page-product-installments-modal v-if="Number.parseInt(activeProduct.pricing.price.amount) > 50"
                :price="activeProduct.pricing.price.amount" />

            <div v-if="!hidePriceHistory && omnibusContent"
                class="pt-xs text-sm tracking-xl">
                <div class="flex flex-row items-center">
                    <v-icon icon="tag"
                        class="mr-xs text-lg" />
                    {{ omnibusContent }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { SimpleProductDataFragment, ViewProductDetailsQuery } from '~~/graphql';

type DetailProduct = Extract<
    ViewProductDetailsQuery['product'],
    { __typename: 'SimpleProduct' }
>;

interface PromotionInfo {
    activeTo?: Date
    hidePriceHistory: boolean
    showEndingTimer: boolean
    showCountdownTextContent: boolean
    promotionTimerContent: string
    isTimedVisibility: boolean
    isCampaignModalEnabled: boolean
    campaignModalContent: string
}

const properties = defineProps<{
    error: boolean
    activeProduct: SimpleProductDataFragment
    dimensions: DetailProduct['dimensions'] | undefined
    pending: boolean
    promotionInfo: PromotionInfo
    hidePriceHistory?: boolean
    lowestPrice?: string
    omnibusContent: string
}>();

const calculateLiterPrice = computed(() => (properties.dimensions?.volume
    && properties.activeProduct.pricing?.price?.amount
    ? (Number.parseFloat(properties.activeProduct.pricing?.price?.amount) / properties.dimensions.volume) * 100
    : undefined));

const { t } = useI18n({
    useScope: 'local',
});

const { format: formatCurrency } = useCurrency();

const isMounted = ref(false);

const {
    isPromotionActive,
    clubPrice,
} = useClubPromotion(computed(() => properties.activeProduct), 'product');

const clubSaving = computed(() => {
    if (clubPrice.value) {
        return properties.activeProduct.pricing.originalPrice
            ? Number(properties.activeProduct.pricing.originalPrice.amount) - clubPrice.value
            : undefined;
    }

    return undefined;
});

const priceRegex = /#price/i;

const omnibusContent = computed<string>(() => (
    properties.omnibusContent
        .replace(
            priceRegex,
            formatCurrency(Number(properties.lowestPrice)),
        )
));

const isWithin48Hours = computed(() => {
    const now = new Date();
    const timeDifference = (properties.promotionInfo.activeTo ?? new Date()).getTime() - now.getTime();
    const hoursDifference = timeDifference / (1000 * 60 * 60);

    return hoursDifference > 0 && hoursDifference <= 48;
});

const timerHasLessThan48hoursRemaining = computed(() => properties.promotionInfo.showEndingTimer
    && properties.promotionInfo.isTimedVisibility
    && isWithin48Hours.value);

// We don't try to show the timer before we've mounted so that there won't be hydration errors on stuff that depends on this if / when the server's time doesn't match the client-side time.
const isCountdownShown = computed(() => isMounted.value
    && properties.promotionInfo.showEndingTimer
    && (!properties.promotionInfo.isTimedVisibility || timerHasLessThan48hoursRemaining.value));

onMounted(() => {
    isMounted.value = true;
});
</script>

<i18n lang="json">
{
    "et": {
        "product-part-payment": "Maksa osade kaupa ",
        "product-part-payment-prefix": "al.",
        "product-part-payment-suffix": "/kuu",
        "regular-price": "Tavahind",
        "lowest-price-in-the-last-30-days": "30 päeva madalaim hind"
    },
    "fi": {
        "product-part-payment": "Maksa erissä ",
        "product-part-payment-prefix": "alk.",
        "product-part-payment-suffix": "/kk",
        "regular-price": "Normaalihinta",
        "lowest-price-in-the-last-30-days": "Alin hinta 30 pv"
    }
}
</i18n>
